import { useEffect, useState } from "preact/hooks"
import { api_fetch, api_fetch_throws } from "@/api"
import { Button } from "@/components/ui/button"
import { Checkbox } from "@/components/ui/checkbox"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select"
import { toast } from "@/components/ui/use-toast"
import { classes } from "@/utils"
import { Loader2 } from "lucide-react"
import { sortLedgerAccountsForSettings, type LedgerAccountDto } from "./ledger_account_manage"
import type { CodingWizPreferences } from "@/accounting/coding_wiz_preferences"

interface AccountingAutoCodeSettingsDto {
  blocked_reason_code_ids_for_simple_rules: string[]
  under_amount_threshold_usd: number | null
  under_amount_reason_code_id: string | null
  early_pay_reason_code_id: string | null
  /** Team CodingWiz defaults; edited in its own section, carried through here untouched. */
  coding_wiz_preferences?: CodingWizPreferences | null
}

interface ReasonCodeSelectProps {
  accounts: LedgerAccountDto[]
  value: string | null
  onChange: (id: string | null) => void
  className?: string
}

/** Ledger account picker with an explicit "None". Used by every optional reason-code setting here. */
function ReasonCodeSelect({ accounts, value, onChange, className }: ReasonCodeSelectProps) {
  return (
    <Select value={value ?? "__none__"} onValueChange={(v: string) => onChange(v === "__none__" ? null : v)}>
      <SelectTrigger className={className}>
        <SelectValue placeholder="Select reason code" />
      </SelectTrigger>
      <SelectContent>
        <SelectItem value="__none__">None</SelectItem>
        {accounts.map((a: LedgerAccountDto) => (
          <SelectItem key={a.id} value={a.id}>
            {a.account_number} — {a.expense_account}
          </SelectItem>
        ))}
      </SelectContent>
    </Select>
  )
}

interface AccountingAutoCodeSettingsSectionProps {
  className?: string
}

export function AccountingAutoCodeSettingsSection({ className }: AccountingAutoCodeSettingsSectionProps) {
  const [accounts, setAccounts] = useState<LedgerAccountDto[]>([])
  const [blocked, setBlocked] = useState<Set<string>>(new Set())
  const [thresholdUsd, setThresholdUsd] = useState<string>("")
  const [underReasonId, setUnderReasonId] = useState<string | null>(null)
  const [earlyPayReasonId, setEarlyPayReasonId] = useState<string | null>(null)
  const [codingWizPrefs, setCodingWizPrefs] = useState<CodingWizPreferences | null>(null)
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)

  useEffect(function load_accounts_and_settings() {
    let cancelled = false
    async function run() {
      setLoading(true)
      try {
        const [settingsRes, accountsData] = await Promise.all([
          api_fetch<AccountingAutoCodeSettingsDto>("/organization/accounting-auto-code-settings"),
          api_fetch_throws<LedgerAccountDto[]>("/reason_codes?include_hidden=true", { use_cache: false }),
        ])
        if (cancelled) {
          return
        }
        setAccounts(sortLedgerAccountsForSettings(accountsData))
        if (settingsRes.ok) {
          const s = settingsRes.value.data
          setBlocked(new Set(s.blocked_reason_code_ids_for_simple_rules))
          setThresholdUsd(
            s.under_amount_threshold_usd != null && Number.isFinite(s.under_amount_threshold_usd)
              ? String(s.under_amount_threshold_usd)
              : "",
          )
          setUnderReasonId(s.under_amount_reason_code_id)
          setEarlyPayReasonId(s.early_pay_reason_code_id)
          setCodingWizPrefs(s.coding_wiz_preferences ?? null)
        } else {
          toast({ title: "Error", description: "Failed to load auto-code settings", variant: "destructive" })
        }
      } catch {
        if (!cancelled) {
          toast({ title: "Error", description: "Failed to load reason codes", variant: "destructive" })
        }
      } finally {
        if (!cancelled) {
          setLoading(false)
        }
      }
    }
    void run()
    return () => {
      cancelled = true
    }
  }, [])

  function toggle_blocked(id: string, next: boolean) {
    setBlocked(prev => {
      const n = new Set(prev)
      if (next) {
        n.add(id)
      } else {
        n.delete(id)
      }
      return n
    })
  }

  async function handleSave() {
    setSaving(true)
    const threshold_parsed =
      thresholdUsd.trim() === "" ? null : Number.parseFloat(thresholdUsd.replace(/,/g, ""))
    const body: AccountingAutoCodeSettingsDto = {
      blocked_reason_code_ids_for_simple_rules: [...blocked],
      under_amount_threshold_usd:
        threshold_parsed != null && Number.isFinite(threshold_parsed) && threshold_parsed > 0 ? threshold_parsed : null,
      under_amount_reason_code_id: underReasonId,
      early_pay_reason_code_id: earlyPayReasonId,
      coding_wiz_preferences: codingWizPrefs,
    }
    const res = await api_fetch<AccountingAutoCodeSettingsDto>("/organization/accounting-auto-code-settings", {
      method: "PUT",
      body,
    })
    if (res.ok) {
      const s = res.value.data
      setBlocked(new Set(s.blocked_reason_code_ids_for_simple_rules))
      setThresholdUsd(
        s.under_amount_threshold_usd != null && Number.isFinite(s.under_amount_threshold_usd)
          ? String(s.under_amount_threshold_usd)
          : "",
      )
      setUnderReasonId(s.under_amount_reason_code_id)
      setEarlyPayReasonId(s.early_pay_reason_code_id)
      toast({ title: "Saved", description: "Auto-code settings updated" })
    } else {
      toast({
        title: "Error",
        description: res.error.message ?? "Failed to save auto-code settings",
        variant: "destructive",
      })
    }
    setSaving(false)
  }

  if (loading) {
    return (
      <div class={classes("flex items-center justify-center py-8", className)}>
        <Loader2 className="h-5 w-5 animate-spin text-gray-400" />
      </div>
    )
  }

  return (
    <div class={classes("border rounded-lg p-6 mt-8 space-y-6", className)}>
      <div>
        <h2 class="text-lg font-semibold">Auto-code &amp; Simple rules</h2>
        <p class="text-sm text-gray-500 mt-1">
          Control batch auto-coding (transactions page and scheduled job) and which reason codes may be stored as
          category Simple rules when coding manually.
        </p>
      </div>

      <div class="space-y-2">
        <Label class="text-base">Reason codes excluded from Simple category rules</Label>
        <p class="text-sm text-gray-500">
          Checked accounts will not create or update a Simple rule when you code a deduction, but splits can still use
          them—including the small-amount rule below.
        </p>
        <div class="max-h-56 overflow-y-auto border rounded-md p-3 space-y-2">
          {accounts.map((a: LedgerAccountDto) => (
            <label key={a.id} class="flex items-start gap-2 cursor-pointer text-sm">
              <Checkbox
                checked={blocked.has(a.id)}
                onCheckedChange={(v: boolean | "indeterminate") => toggle_blocked(a.id, v === true)}
              />
              <span>
                <span class="font-mono text-xs text-gray-600">{a.account_number}</span>{" "}
                <span class="text-gray-800">{a.expense_account}</span>
              </span>
            </label>
          ))}
        </div>
      </div>

      <div class="space-y-3 border-t pt-6">
        <Label class="text-base">Small-amount auto-code (optional)</Label>
        <p class="text-sm text-gray-500">
          When batch auto-coding, if the deduction&apos;s absolute invoice amount is <strong>strictly less than</strong>{" "}
          this threshold (USD), assign the selected reason code instead of the category Simple rule. This does not
          create a Simple rule.
        </p>
        <div class="flex flex-col sm:flex-row gap-4 sm:items-end">
          <div class="space-y-1 flex-1">
            <Label htmlFor="under_amount_threshold">Threshold (USD)</Label>
            <Input
              id="under_amount_threshold"
              type="number"
              min={0}
              step="0.01"
              placeholder="e.g. 250"
              value={thresholdUsd}
              onInput={(e: Event) => setThresholdUsd((e.target as HTMLInputElement).value)}
            />
          </div>
          <div class="space-y-1 flex-1">
            <Label>Reason code for amounts under threshold</Label>
            <ReasonCodeSelect accounts={accounts} value={underReasonId} onChange={setUnderReasonId} />
          </div>
        </div>
      </div>

      <div class="space-y-3 border-t pt-6">
        <Label class="text-base">Early-payment discount account (optional)</Label>
        <p class="text-sm text-gray-500">
          Invoices paid early carry a terms discount but no category, so they never reach the Simple rules and had to be
          coded by hand on every check. Set the account here and batch auto-coding books each discount to it — including
          a UNFI <span class="font-mono text-xs">PP</span> advance, which is an invoice paid early. An advance we
          can&apos;t match to its invoice is left alone rather than coded: it is cash, not spend.
        </p>
        <div class="sm:max-w-md">
          <ReasonCodeSelect accounts={accounts} value={earlyPayReasonId} onChange={setEarlyPayReasonId} />
        </div>
      </div>

      <div class="flex justify-end">
        <Button type="button" onClick={handleSave} disabled={saving}>
          {saving ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : null}
          Save
        </Button>
      </div>
    </div>
  )
}
